import React, { useContext, useMemo, useState } from 'react'
import { HiOutlinePencil, HiPlus, HiOutlineTrash } from 'react-icons/hi'
import api from '../../utils/api'
import { CaseDetailContext } from './CaseDetailContext'
// Party-level operations (add / remove a whole party) re-divide invoices and
// send email via scale-be's orchestrators, so they go through cm-be's party
// proxy rather than writing rows here.
import ModifyPartiesModal from './parties/ModifyPartiesModal'
import { hasPaidInvoiceOnCase } from './parties/partyUtils'
import { normalizeEmail, isValidEmail, emailError } from '../../utils/emailAddress'

/* A party's alternate emails, as the extra rows the mediator portal shows.
 *
 * One deliberate difference from that portal: an address that already has a row
 * of its own in the same party is left out, so nobody is listed twice. Case 4955
 * carries the same people BOTH as alternates on the lead row and as rows added
 * by hand, and showing both copies is what made the page unreadable. The
 * comparison ignores case, because the two sources are typed by different
 * people. Returns [] for a party with no alternates, which is every party that
 * was not added after booking. */
const alternateRowsFor = (party, rowsInParty) => {
    const alternates = Array.isArray(party?.alternate_emails)
        ? party.alternate_emails
        : []
    const norm = (e) => String(e || '').trim().toLowerCase()
    const asRows = new Set(rowsInParty.map((r) => norm(r.email)).filter(Boolean))
    const seen = new Set()
    return alternates
        .map((e) => String(e || '').trim())
        .filter((e) => {
            const key = norm(e)
            if (!key || asRows.has(key) || seen.has(key)) return false
            seen.add(key)
            return true
        })
}

/**
 * CaseParticipants — Parties section, matching the mediator portal.
 *
 * Layout mirrors scale-portal's UnifiedParticipantsTable: ONE table for the
 * whole case, with each party introduced by a grey band carrying the party
 * label and its three actions (edit participants / add participant / delete),
 * followed by that party's NAME · EMAIL · PHONE · DESIGNATION · ACTION rows.
 *
 * Party groups, in Scale's order:
 *   Plaintiff: <name>                (primary)
 *   Plaintiff N: <name>              (one per additional plaintiff party)
 *   Defendant: <name>                (primary)
 *   Defendant N: <name>              (one per additional defendant party)
 */

function ExistingParticipantsModal({ isOpen, onClose, conflicts }) {
    if (!isOpen) return null
    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50" onClick={onClose}>
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 p-6" onClick={(e) => e.stopPropagation()}>
                <h5 className="text-lg font-semibold mb-2">Existing Participants</h5>
                <p className="text-sm text-gray-600 mb-3">
                    The email{conflicts.length > 1 ? 's' : ''} below {conflicts.length > 1 ? 'are' : 'is'} already in use on this case:
                </p>
                <ul className="divide-y divide-gray-100 mb-4">
                    {conflicts.map((c, i) => (
                        <li key={i} className="py-2 text-sm">
                            <span className="font-medium text-gray-900">{c.name || '—'}</span>
                            <span className="text-gray-500"> · {c.email}</span>
                        </li>
                    ))}
                </ul>
                <div className="flex justify-end">
                    <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg bg-gray-900 text-white text-sm font-medium">
                        Close
                    </button>
                </div>
            </div>
        </div>
    )
}

const CELL = 'px-6 py-3 text-sm text-gray-700 align-middle'
const HEAD = 'px-6 py-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500 text-left bg-white'
const INPUT = 'w-full rounded-md border border-gray-200 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900/10'

/**
 * One party group: grey band + column headers + participant rows.
 * Editing is per group, exactly as in Scale — the pencil turns the group's
 * rows into inputs and swaps the icons for Save / Cancel.
 */
function PartyGroup({
    group,
    caseId,
    allParticipants,
    onChanged,
    onToast,
    onRemoveParty,
    // Whole-party removal is locked once a payment exists on the case. Only
    // affects the party-level trash icon; participant contacts stay editable,
    // which is what the mediator portal does too.
    partyActionsDisabled = false,
    partyActionsTooltip = '',
}) {
    const [editing, setEditing] = useState(false)
    const [deleteMode, setDeleteMode] = useState(false)
    const [draft, setDraft] = useState([])
    const [pendingNew, setPendingNew] = useState(null)
    const [busy, setBusy] = useState(false)
    const [conflictModal, setConflictModal] = useState(null)

    const rows = group.rows

    const startEdit = () => {
        setDraft(rows.map((r) => ({ ...r })))
        setPendingNew(null)
        setEditing(true)
        setDeleteMode(false)
    }
    const cancelEdit = () => {
        setEditing(false)
        setDraft([])
        setPendingNew(null)
    }
    const openAddRow = () => {
        if (!editing) startEdit()
        setPendingNew({ name: '', email: '', phone: '', designation: '', alternate_emails: [] })
    }

    const checkDuplicate = (email) => {
        if (!email) return null
        const lower = email.toLowerCase()
        const conflicts = (allParticipants || []).filter((x) => x.email && x.email.toLowerCase() === lower)
        return conflicts.length ? conflicts : null
    }

    // Rows under an additional party live in `additional_participants`, not
    // `participants`. Ids overlap between the two tables, so every write has to
    // say which one it means or it can land on an unrelated row.
    const tableParams = group.isAdditional ? { is_additional_party: true } : undefined

    const isRealRow = (r) => typeof r.id !== 'string'

    const saveAll = async () => {
        try {
            setBusy(true)

            /* Nothing is sent until every address is one the mail provider will
             * accept. This page had no address validation at all, which is how
             * a participant's NAME ("heather davis", case 4932) and a CC with a
             * trailing bracket ("adame@maralawfirm.com)", case 4204) came to be
             * stored — each one then made Loops refuse an entire email, while
             * email_logs still recorded it as sent.
             *
             * Repairable text is repaired rather than rejected: surrounding
             * spaces, angle brackets and a trailing bracket are stripped and the
             * clean address is what gets saved. A name has no correct repair, so
             * it stops the save and says so. */
            const toSave = [...draft, ...(pendingNew ? [pendingNew] : [])]
            const badRow = toSave.find(
                (r) => r && (r.name || r.email || r.phone) &&
                    !isValidEmail(normalizeEmail(r.email))
            )
            if (badRow) {
                onToast?.({
                    type: 'error',
                    message: `${badRow.name || 'This participant'}: ${emailError(badRow.email)}`,
                })
                setBusy(false)
                return
            }
            setDraft((prev) => prev.map((r) => ({ ...r, email: normalizeEmail(r.email) })))

            const realRows = rows.filter(isRealRow)
            const realDraft = draft
                .map((r) => ({ ...r, email: normalizeEmail(r.email) }))
                .filter(isRealRow)
            const originalById = new Map(realRows.map((r) => [r.id, r]))
            const draftIds = new Set(realDraft.map((r) => r.id))
            const promises = []

            for (const r of realRows) {
                if (!draftIds.has(r.id)) {
                    promises.push(api.delete(`/cases/${caseId}/participants/${r.id}`, { params: tableParams }))
                }
            }
            for (const r of realDraft) {
                const orig = originalById.get(r.id)
                if (!orig) continue
                const changed =
                    orig.name !== r.name ||
                    orig.email !== r.email ||
                    orig.phone !== r.phone ||
                    orig.designation !== r.designation
                if (changed) {
                    promises.push(api.put(`/cases/${caseId}/participants/${r.id}`, {
                        name: r.name, email: r.email, phone: r.phone, designation: r.designation,
                        alternate_emails: r.alternate_emails || [],
                    }, { params: tableParams }))
                }
            }

            if (pendingNew && (pendingNew.name || pendingNew.email || pendingNew.phone)) {
                const dup = checkDuplicate(pendingNew.email)
                if (dup) { setConflictModal(dup); setBusy(false); return }
                promises.push(api.post(`/cases/${caseId}/participants`, {
                    clientId: group.clientId,
                    name: pendingNew.name,
                    email: normalizeEmail(pendingNew.email),
                    phone: pendingNew.phone,
                    designation: pendingNew.designation || `${group.sideLabel} party`,
                    alternate_emails: pendingNew.alternate_emails || [],
                    // Additional parties keep their participants in a separate
                    // table, keyed to the primary party they hang off.
                    isAdditionalParty: group.isAdditional,
                    primaryPartyId: group.primaryPartyId ?? null,
                }))
            }

            await Promise.all(promises)
            onToast?.({ type: 'success', message: 'Participants saved' })
            cancelEdit()
            onChanged?.()
        } catch (err) {
            onToast?.({
                type: 'error',
                message: err?.response?.data?.message || 'Failed to save participants',
            })
        } finally {
            setBusy(false)
        }
    }

    const deleteParticipant = async (row) => {
        try {
            setBusy(true)
            await api.delete(`/cases/${caseId}/participants/${row.id}`, { params: tableParams })
            onToast?.({ type: 'success', message: 'Participant removed' })
            onChanged?.()
        } catch (err) {
            onToast?.({
                type: 'error',
                message: err?.response?.data?.message || 'Failed to remove participant',
            })
        } finally {
            setBusy(false)
        }
    }

    const updateDraftRow = (i, patch) =>
        setDraft((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)))

    const deletableRows = rows.filter(isRealRow)
    const iconBtn = 'p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-200 transition-colors'

    return (
        <>
            {/* Party band */}
            <tr className="bg-gray-100">
                <td colSpan={4} className="py-2.5 px-6">
                    <span className="text-sm font-bold text-gray-800">
                        {group.label}: {group.partyName || '—'}
                    </span>
                </td>
                <td className="py-2.5 px-6">
                    <div className="flex justify-end items-center gap-2">
                        {editing ? (
                            <>
                                <button type="button" disabled={busy} onClick={saveAll}
                                    className="rounded-md bg-teal-800 px-3.5 py-1.5 text-xs font-medium text-white disabled:opacity-50">
                                    {busy ? 'Saving…' : 'Save'}
                                </button>
                                <button type="button" disabled={busy} onClick={cancelEdit}
                                    className="rounded-md border border-gray-200 px-3.5 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50">
                                    Cancel
                                </button>
                            </>
                        ) : (
                            <>
                                <button type="button" title="Edit participants" onClick={startEdit} className={iconBtn}>
                                    <HiOutlinePencil size={16} />
                                </button>
                                <button type="button" title="Add participant" onClick={openAddRow} className={iconBtn}>
                                    <HiPlus size={16} />
                                </button>
                                <button
                                    type="button"
                                    title={
                                        group.isAdditional
                                            ? partyActionsDisabled
                                                ? partyActionsTooltip
                                                : 'Remove this party'
                                            : deleteMode
                                            ? 'Done deleting'
                                            : deletableRows.length
                                            ? 'Delete participants'
                                            : 'No participants to delete'
                                    }
                                    disabled={
                                        group.isAdditional
                                            ? partyActionsDisabled
                                            : !deleteMode && deletableRows.length === 0
                                    }
                                    onClick={() =>
                                        group.isAdditional ? onRemoveParty?.(group) : setDeleteMode((v) => !v)
                                    }
                                    className={iconBtn + ' disabled:opacity-30 disabled:cursor-not-allowed'}
                                >
                                    <HiOutlineTrash size={16} />
                                </button>
                            </>
                        )}
                    </div>
                </td>
            </tr>

            {/* Column headers, repeated per group as Scale does */}
            <tr>
                <th className={HEAD}>Name</th>
                <th className={HEAD}>Email</th>
                <th className={HEAD}>Phone</th>
                <th className={HEAD}>Designation</th>
                <th className={HEAD}><div className="flex justify-end">Action</div></th>
            </tr>

            {/* Rows */}
            {editing ? (
                <>
                    {draft.map((p, i) => (
                        <tr key={p.id} className="border-b border-gray-100">
                            <td className={CELL}>
                                <input className={INPUT} value={p.name || ''} onChange={(e) => updateDraftRow(i, { name: e.target.value })} placeholder="Name" />
                            </td>
                            <td className={CELL}>
                                <input className={INPUT} value={p.email || ''} onChange={(e) => updateDraftRow(i, { email: e.target.value })} placeholder="Email" />
                            </td>
                            <td className={CELL}>
                                <input className={INPUT} value={p.phone || ''} onChange={(e) => updateDraftRow(i, { phone: e.target.value })} placeholder="Phone" />
                            </td>
                            <td className={CELL}>
                                <input className={INPUT} value={p.designation || ''} onChange={(e) => updateDraftRow(i, { designation: e.target.value })} placeholder="Designation" />
                            </td>
                            <td className={CELL}>
                                <div className="flex justify-end">
                                    {isRealRow(p) && (
                                        <button type="button" onClick={() => setDraft((prev) => prev.filter((_, idx) => idx !== i))}
                                            className="text-red-500 hover:text-red-700 text-xs font-medium">
                                            Remove
                                        </button>
                                    )}
                                </div>
                            </td>
                        </tr>
                    ))}
                    {pendingNew && (
                        <tr className="border-b border-gray-100 bg-gray-50/60">
                            <td className={CELL}>
                                <input className={INPUT} value={pendingNew.name} onChange={(e) => setPendingNew({ ...pendingNew, name: e.target.value })} placeholder="New name" />
                            </td>
                            <td className={CELL}>
                                <input className={INPUT} type="email" value={pendingNew.email} onChange={(e) => setPendingNew({ ...pendingNew, email: e.target.value })} placeholder="New email" />
                            </td>
                            <td className={CELL}>
                                <input className={INPUT} value={pendingNew.phone} onChange={(e) => setPendingNew({ ...pendingNew, phone: e.target.value })} placeholder="Phone" />
                            </td>
                            <td className={CELL}>
                                <input className={INPUT} value={pendingNew.designation} onChange={(e) => setPendingNew({ ...pendingNew, designation: e.target.value })} placeholder="Designation" />
                            </td>
                            <td className={CELL}>
                                <div className="flex justify-end">
                                    <button type="button" onClick={() => setPendingNew(null)} className="text-gray-400 hover:text-gray-600 text-xs font-medium">
                                        Cancel
                                    </button>
                                </div>
                            </td>
                        </tr>
                    )}
                </>
            ) : rows.length === 0 ? (
                <tr className="border-b border-gray-100">
                    <td className={CELL + ' text-gray-400 italic'} colSpan={5}>No participants yet.</td>
                </tr>
            ) : (
                rows.map((p) => (
                    <React.Fragment key={p.id}>
                        <tr className="border-b border-gray-100">
                            <td className={CELL}>{p.name || '—'}</td>
                            <td className={CELL}>
                                {p.email ? <span className="text-gray-500">{p.email}</span> : '—'}
                            </td>
                            <td className={CELL}>{p.phone || '—'}</td>
                            <td className={CELL}>{p.designation || '—'}</td>
                            <td className={CELL}>
                                <div className="flex justify-end">
                                    {deleteMode && isRealRow(p) && (
                                        <button type="button" disabled={busy} onClick={() => deleteParticipant(p)}
                                            className="text-red-500 hover:text-red-700 disabled:opacity-50" title="Delete participant">
                                            <HiOutlineTrash size={15} />
                                        </button>
                                    )}
                                </div>
                            </td>
                        </tr>
                        {/* An added party's extra people are stored as alternate_emails on
                            that party's row, not as rows of their own. The mediator portal
                            expands them into rows (expandAlternateParticipants in its
                            utils); this page did not, so a case manager saw only the lead
                            and re-added people who were already copied on every email for
                            that side (case 4955).

                            Drawn the same way the mediator portal draws them — an ordinary
                            row carrying only the email, with the other columns blank — so
                            the two portals read identically. Read-only here: they are never
                            put into the edit draft, so an edit or a delete cannot touch
                            them. */}
                        {alternateRowsFor(p, rows).map((email) => (
                            <tr key={`alt-${p.id}-${email}`} className="border-b border-gray-100">
                                <td className={CELL}>—</td>
                                <td className={CELL}>
                                    <span className="text-gray-500">{email}</span>
                                </td>
                                <td className={CELL}>—</td>
                                <td className={CELL}>—</td>
                                <td className={CELL}></td>
                            </tr>
                        ))}
                    </React.Fragment>
                ))
            )}

            <ExistingParticipantsModal isOpen={!!conflictModal} onClose={() => setConflictModal(null)} conflicts={conflictModal || []} />
        </>
    )
}

export default function CaseParticipants({ onToast, onRefresh }) {
    const { caseData, plaintiffPartyLabel, defendantPartyLabel, is_cancled_case } =
        useContext(CaseDetailContext)
    // ONE "Modify parties" modal for the whole section, opened in 'add' or
    // 'remove' mode — same as the mediator portal, whose wizard carries the
    // Add/Remove toggle in its own header.
    const [partyModalOpen, setPartyModalOpen] = useState(false)
    const [partyModalMode, setPartyModalMode] = useState('add')
    // Pre-selects a party when the modal is opened from a row's trash icon.
    const [removePreselectKey, setRemovePreselectKey] = useState(null)

    const participants = Array.isArray(caseData?.participants) ? caseData.participants : []
    const pid = caseData?.plaintiff?.client_id || caseData?.plaintiff?.clientId
    const did = caseData?.defendant?.client_id || caseData?.defendant?.clientId
    // Memoised so the groups useMemo below has stable dependencies — a fresh
    // [] literal every render would rebuild every group on every render.
    const plaintiffAdditional = useMemo(
        () => (Array.isArray(caseData?.plaintiff?.additionalParties) ? caseData.plaintiff.additionalParties : []),
        [caseData]
    )
    const defendantAdditional = useMemo(
        () => (Array.isArray(caseData?.defendant?.additionalParties) ? caseData.defendant.additionalParties : []),
        [caseData]
    )

    const isPlaintiffSide = (p) => {
        if (pid && p.client_id === pid) return true
        if (did && p.client_id === did) return false
        const d = (p.designation || '').toLowerCase()
        if (d.includes('defendant') || d.includes('defender') || d.includes('respondent')) return false
        return true
    }

    // A primary party's rows are exactly its `participants` rows — matching
    // Scale, which shows the party name only in the band and never repeats it
    // as a table row.
    //
    // Note there is deliberately NO exclusion by additional-party client_id
    // here: an additional party can share a client row with a primary one (on
    // case 2841 "Plaintiff 4" and the defendant are both client 201364), so
    // excluding those ids would hide the primary party's own people.
    // Additional parties read from their own table instead, so there is no
    // double-counting to guard against.
    const rowsForPrimary = (side) => {
        const isP = side === 'plaintiff'
        const targetId = isP ? pid : did
        return participants.filter((p) =>
            targetId ? p.client_id === targetId : (isP ? isPlaintiffSide(p) : !isPlaintiffSide(p))
        )
    }

    // An additional party's people come from `additional_participants`, which
    // cm-be attaches as `ap.participants`. They must NOT be derived by
    // filtering the case-wide participants list on client_id: an additional
    // party can REUSE an existing client row, so on some cases a party and the
    // defendant share a client_id and the filter mixes their people together.
    const rowsForAdditional = (ap) => (Array.isArray(ap.participants) ? ap.participants : [])

    // Group order matches Scale: primary plaintiff, its additional parties,
    // then primary defendant and its additional parties.
    const groups = useMemo(() => {
        const out = []
        out.push({
            key: 'plaintiff-primary',
            // Matches collectRemovableParties' key format so the row trash icon
            // can pre-select this party in the modal.
            removeKey: 'plaintiff-primary',
            label: plaintiffPartyLabel || 'Plaintiff',
            partyName: caseData?.plaintiff?.name,
            clientId: pid,
            sideLabel: 'plaintiff',
            isAdditional: false,
            rows: rowsForPrimary('plaintiff'),
        })
        plaintiffAdditional.forEach((ap, i) => {
            out.push({
                key: `plaintiff-add-${i}`,
                removeKey: `plaintiff-additional-${ap.case_party_id ?? ap.id}`,
                label: ap.position || `${plaintiffPartyLabel || 'Plaintiff'} ${i + 2}`,
                partyName: ap.name || ap.client?.name,
                clientId: ap.client_id || ap.client?.client_id,
                casePartyId: ap.case_party_id ?? ap.id,
                primaryPartyId: ap.primary_party_id ?? pid,
                sideLabel: 'plaintiff',
                isAdditional: true,
                rows: rowsForAdditional(ap),
            })
        })
        out.push({
            key: 'defendant-primary',
            removeKey: 'defendant-primary',
            label: defendantPartyLabel || 'Defendant',
            partyName: caseData?.defendant?.name,
            clientId: did,
            sideLabel: 'defendant',
            isAdditional: false,
            rows: rowsForPrimary('defendant'),
        })
        defendantAdditional.forEach((ap, i) => {
            out.push({
                key: `defendant-add-${i}`,
                removeKey: `defendant-additional-${ap.case_party_id ?? ap.id}`,
                label: ap.position || `${defendantPartyLabel || 'Defendant'} ${i + 2}`,
                partyName: ap.name || ap.client?.name,
                clientId: ap.client_id || ap.client?.client_id,
                casePartyId: ap.case_party_id ?? ap.id,
                primaryPartyId: ap.primary_party_id ?? did,
                sideLabel: 'defendant',
                isAdditional: true,
                rows: rowsForAdditional(ap),
            })
        })
        return out
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [caseData, participants, plaintiffAdditional, defendantAdditional, plaintiffPartyLabel, defendantPartyLabel])

    // The modal builds its own removable-party list (collectRemovableParties),
    // so this is only the gate for the header button.
    const hasRemovableParty = groups.length > 0

    // THE PAID GATE LIVES HERE, AND ONLY HERE.
    //
    // Once any party has paid, Add Party / Remove Party are simply disabled with
    // a tooltip saying why. The modal carries no lock screen of its own — it is
    // never reachable on a locked case, so a second refusal inside it would only
    // be a screen the user has to read and dismiss.
    //
    // hasPaidInvoiceOnCase is the same predicate the mediator portal uses:
    // status paid/paid_manually AND amount > 0, which deliberately ignores the
    // $0 paid_manually placeholders hourly bookings create at booking time.
    const partiesLockedByPayment = useMemo(() => hasPaidInvoiceOnCase(caseData), [caseData])
    const partyActionsDisabled = is_cancled_case || partiesLockedByPayment

    const addPartyTooltip = is_cancled_case
        ? 'Case is canceled'
        : partiesLockedByPayment
          ? "Parties can't be added once a payment has been made on this case. Please reach out to support@scalemediation.com for additional assistance."
          : ''
    const removePartyTooltip = is_cancled_case
        ? 'Case is canceled'
        : partiesLockedByPayment
          ? "Parties can't be removed once a payment has been made on this case. Please reach out to support@scalemediation.com for additional assistance."
          : !hasRemovableParty
            ? 'No removable parties on this case.'
            : ''

    // "Invoices exist in the finances table" — the same boolean the Finances
    // section keys off. The modal decides from THIS whether to show the
    // invoice-split step, so both surfaces agree about the same case.
    const payments = Array.isArray(caseData?.payments) ? caseData.payments : []
    const hasInvoiceRecords = payments.length > 0

    const openPartyModal = (nextMode, preselect = null) => {
        setPartyModalMode(nextMode)
        setRemovePreselectKey(preselect)
        setPartyModalOpen(true)
    }

    return (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            {/* Section header */}
            <div className="px-6 py-5 flex items-start justify-between border-b border-gray-100">
                <div>
                    <h3 className="text-lg font-semibold text-gray-900">Parties</h3>
                    <p className="text-sm text-gray-500 mt-0.5">Plaintiffs, defendants and their participants</p>
                </div>
                <div className="flex items-center gap-2">
                    {/* The title sits on a wrapper: a disabled button swallows
                        pointer events, so the tooltip would never show on it. */}
                    <span className={partyActionsDisabled || !hasRemovableParty ? 'cursor-not-allowed' : ''} title={removePartyTooltip || undefined}>
                        <button
                            type="button"
                            onClick={() => openPartyModal('remove')}
                            disabled={partyActionsDisabled || !hasRemovableParty}
                            className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:border-gray-300 hover:text-gray-900 disabled:opacity-40 disabled:cursor-not-allowed disabled:pointer-events-none"
                        >
                            Remove Party
                        </button>
                    </span>
                    <span className={partyActionsDisabled ? 'cursor-not-allowed' : ''} title={addPartyTooltip || undefined}>
                        <button
                            type="button"
                            onClick={() => openPartyModal('add')}
                            disabled={partyActionsDisabled}
                            className="px-4 py-2 rounded-lg bg-gray-900 text-white text-sm font-medium hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed disabled:pointer-events-none"
                        >
                            Add Party
                        </button>
                    </span>
                </div>
            </div>

            <div className="overflow-x-auto">
                <table className="w-full" style={{ minWidth: 780 }}>
                    <tbody>
                        {groups.map((g) => (
                            <PartyGroup
                                key={g.key}
                                group={g}
                                caseId={caseData?.id}
                                allParticipants={participants}
                                onChanged={onRefresh}
                                onToast={onToast}
                                onRemoveParty={(grp) => openPartyModal('remove', grp.removeKey)}
                                partyActionsDisabled={partyActionsDisabled}
                                partyActionsTooltip={removePartyTooltip}
                            />
                        ))}
                    </tbody>
                </table>
            </div>

            <ModifyPartiesModal
                isOpen={partyModalOpen}
                onClose={() => {
                    setPartyModalOpen(false)
                    setRemovePreselectKey(null)
                }}
                caseId={caseData?.id}
                mode={partyModalMode}
                preselectRemoveKey={removePreselectKey}
                hasInvoiceRecords={hasInvoiceRecords}
                onToast={onToast}
                onRefresh={onRefresh}
            />
        </div>
    )
}
